import React, { useEffect, useState, useMemo } from "react";
import DataTable from "react-data-table-component";
import { AiOutlineDownload, AiOutlineFileDone, AiOutlineCheckCircle } from "react-icons/ai";
import userAuthService from '../services/userAuthService';

// ---- REAL API Section ----
// Real API function to fetch final copy details
export async function getFinalCopyDetails() {
  const response = await userAuthService.userApiCall('/tax-returns/final/');
  
  if (!response.ok) {
    throw new Error('Failed to fetch final copies');
  }
  
  const data = await response.json();
  return data.data || [];
}

// Real API function to download the file
export async function downloadFinalCopyFile({ id, filename }) {
  const response = await userAuthService.userApiCall(`/tax-returns/final/${id}/download/`);
  
  if (!response.ok) {
    throw new Error('Failed to download final file');
  }
  
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

// API function to mark final copy as received
export async function markFinalCopyAsReceived(taxReturnId) {
  const response = await userAuthService.userApiCall(`/tax-returns/final/${taxReturnId}/received/`, {
    method: 'POST'
  });
  
  if (!response.ok) {
    throw new Error('Failed to mark as received');
  }
  
  return await response.json();
}

export default function DownloadTaxReturnFinalCopy() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showReceivedDialog, setShowReceivedDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");

  // Pagination & Sorting
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");

  // Format date to CST timezone
  function formatToCST(dateString) {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      timeZone: 'America/Chicago',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  }

  // Fetch data from API on component mount or page/perPage/sort change
  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      let result = await getFinalCopyDetails();

      // Process the data to add required fields
      // Use type_display from API (which includes category prefix: "Final Copy", "Amendment Final Copy", or "Notice Final Copy")
      // IMPORTANT: Preserve is_received status from API - each tax return has its own status
      const processedData = result.map(item => ({
        ...item,
        category: item.type_display || "Final Copy", // Use type_display from API (includes category prefix)
        datetime: formatToCST(item.created_at), // Convert to CST timezone
        filename: item.filename || 'Unknown file', // Ensure filename exists
        // Preserve is_received from API - this is specific to each tax return (regular vs amendment are separate)
        is_received: item.is_received || false
      }));

      // Sorting
      if (sortColumn) {
        processedData.sort((a, b) => {
          const aVal = a[sortColumn];
          const bVal = b[sortColumn];
          if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
          if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
          return 0;
        });
      }
      setData(processedData);
      setLoading(false);
      setPage(1); // reset to first page on new fetch or sort
    }
    fetchData();
  }, [sortColumn, sortDirection]);

  // Paginated rows for current page
  const paginatedRows = useMemo(() => {
    const start = (page - 1) * perPage;
    return data.slice(start, start + perPage);
  }, [data, page, perPage]);

  // Columns definition for react-data-table-component
  const columns = useMemo(() => [
    {
      name: "S.No",
      width: "60px",
      cell: (row, index) => (page - 1) * perPage + index + 1,
      sortable: false,
      style: { fontWeight: 600, color: "#433ca" },
    },
    {
      name: "Category",
      selector: row => row.category,
      sortable: true,
      minWidth: "120px",
      style: { fontWeight: 500 },
    },
    {
      name: "Year",
      selector: row => row.year,
      sortable: true,
      width: "90px",
      style: { textAlign: "center" },
    },
    {
      name: "File Name",
      selector: row => row.filename,
      sortable: true,
      minWidth: "200px",
      cell: row => (
        <button
          onClick={() => handleDownload(row)}
          style={{
            background: "none",
            border: "none",
            color: "#2563eb",
            cursor: "pointer",
            fontWeight: 600,
            fontSize: 14,
            display: "flex",
            alignItems: "center",
            gap: 5,
          }}
          title={`Download ${row.filename}`}
          disabled={loading}
        >
          <AiOutlineDownload /> {row.filename}
        </button>
      ),
    },
    {
      name: "Date Time",
      selector: row => row.datetime,
      sortable: true,
      minWidth: "150px",
      style: { color: "#555", fontSize: 14 },
    },
    {
      name: "Actions",
      cell: row => (
        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={() => openReceivedDialog(row)}
            aria-label="Mark as received"
            style={{
              background: row.is_received ? "#a1a1aa" : "#10b981",
              color: "#fff",
              borderRadius: 6,
              border: "none",
              padding: "6px 12px",
              fontSize: 13,
              cursor: row.is_received ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: 4,
              fontWeight: 500
            }}
            disabled={row.is_received}
            title={row.is_received ? "Already received" : "Mark as received"}
          >
            <AiOutlineCheckCircle /> {row.is_received ? "Received" : "Received"}
          </button>
        </div>
      ),
      ignoreRowClick: true,
    },
  ], [page, perPage, loading]);

  // Handle download click
  async function handleDownload(row) {
    setLoading(true);
    try {
      await downloadFinalCopyFile({ id: row.id, filename: row.filename });
    } catch (error) {
      console.error('Download failed:', error);
    }
    setLoading(false);
  }

  // Open received dialog
  function openReceivedDialog(row) {
    if (row.is_received) return;
    setSelectedRow(row);
    setShowReceivedDialog(true);
  }

  // Close received dialog
  function closeReceivedDialog() {
    setShowReceivedDialog(false);
    setSelectedRow(null);
  }

  // Handle received confirmation
  async function handleReceivedConfirmation() {
    if (!selectedRow) return;
    
    try {
      await markFinalCopyAsReceived(selectedRow.id);
      
      // Update the data to mark as received
      setData(prevData => 
        prevData.map(item => 
          item.id === selectedRow.id 
            ? { ...item, is_received: true }
            : item
        )
      );
      
      // Show success message
      setSuccessMessage("Successfully received!");
      setTimeout(() => {
        setSuccessMessage("");
      }, 3000);
      
      closeReceivedDialog();
    } catch (error) {
      console.error('Failed to mark as received:', error);
      alert('Failed to mark as received. Please try again.');
    }
  }

  // Data table custom styles for clean look
  const customStyles = {
    rows: {
      style: {
        fontSize: 14,
        backgroundColor: "#f9fafb",
      },
      highlightOnHoverStyle: {
        backgroundColor: "#dbeafe",
        color: "#1e3a8a",
        cursor: "pointer",
      },
    },
    headRow: {
      style: {
        backgroundColor: "#e0e7ff",
        minHeight: "50px",
      },
    },
    headCells: {
      style: {
        color: "#1e40af",
        fontWeight: "700",
        fontSize: "15px",
        paddingLeft: "12px",
        paddingRight: "12px",
      },
    },
    cells: {
      style: {
        paddingLeft: "12px",
        paddingRight: "12px",
      },
    },
    pagination: {
      style: {
        color: "#1e40af",
        fontWeight: 600,
      },
    },
  };

  return (
    <section style={{ userSelect: "none" }}>
      {/* Success Message */}
      {successMessage && (
        <div style={{
          background: "#dcfce7",
          border: "1px solid #16a34a",
          color: "#166534",
          padding: "12px 16px",
          borderRadius: "8px",
          marginBottom: "16px",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          fontSize: "14px",
          fontWeight: "500"
        }}>
          <AiOutlineCheckCircle style={{ fontSize: "18px" }} />
          {successMessage}
        </div>
      )}

      <h2
        style={{
          // display: "flex",
          // alignItems: "center",
          // gap: 8,
          // fontSize: 24,
          // fontWeight: 700,
          // color: "#433ca",
          // marginBottom: 22,


          fontSize: 38,
          marginRight: 10,
          color: "#4338ca",
          verticalAlign: "middle",
          filter: "drop-shadow(0px 1px 4px #b5beff77)",
        }}
      >
        <AiOutlineFileDone />
        Download Final Copy
      </h2>
      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: 20,
          padding: "20px",
          boxShadow: "0 5px 25px rgb(165 180 252 / 0.15)",
        }}
      >
        <DataTable
          columns={columns}
          data={paginatedRows}
          progressPending={loading}
          pagination
          paginationServer={false}
          paginationTotalRows={data.length}
          paginationDefaultPage={page}
          paginationPerPage={perPage}
          paginationRowsPerPageOptions={[10, 20, 30, 40, 50]}
          onChangePage={setPage}
          onChangeRowsPerPage={setPerPage}
          sortServer={true}
          onSort={(column, sortDirection) => {
            setSortColumn(column.selector ? column.selector : column.name);
            setSortDirection(sortDirection);
          }}
          highlightOnHover
          striped
          persistTableHead
          responsive
          noHeader
          customStyles={customStyles}
        />
      </div>

      {/* Received Confirmation Dialog */}
      {showReceivedDialog && selectedRow && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000
        }}>
          <div style={{
            background: "#fff",
            borderRadius: "12px",
            padding: "24px",
            maxWidth: "400px",
            width: "90%",
            boxShadow: "0 10px 25px rgba(0, 0, 0, 0.2)"
          }}>
            <h3 style={{
              margin: "0 0 16px 0",
              fontSize: "18px",
              fontWeight: "600",
              color: "#374151"
            }}>
              Confirm Receipt
            </h3>
            
            <p style={{
              margin: "0 0 16px 0",
              fontSize: "14px",
              color: "#6b7280"
            }}>
              Have you received the final copy for <strong>{selectedRow.filename}</strong>?
            </p>
            
            <div style={{
              display: "flex",
              gap: "12px",
              justifyContent: "flex-end"
            }}>
              <button
                onClick={closeReceivedDialog}
                style={{
                  padding: "8px 16px",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  background: "#fff",
                  color: "#374151",
                  fontSize: "14px",
                  cursor: "pointer"
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleReceivedConfirmation}
                style={{
                  padding: "8px 16px",
                  border: "none",
                  borderRadius: "6px",
                  background: "#10b981",
                  color: "#fff",
                  fontSize: "14px",
                  cursor: "pointer"
                }}
              >
                Yes, Received
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
